/**
 * Reusable pipeline for stores that own a single-in-flight fetch: sequence-
 * numbered supersede + timeout + abort + error classification. Subclasses
 * implement {@link applyResponse} to consume the successful payload and
 * {@link classifyError} to turn a rejection into a user-visible message.
 * Subclasses use {@link runWithSupersede} to wire their `load()` in one call.
 */
export abstract class SupersedableStore<TResponse> {
  /** In-flight AbortController for the newest request; `#beginRequest` supersedes any previous one. */
  #controller: AbortController | undefined;

  /** Monotonic request counter; a resolution whose `mySeq !== #sequence` has been superseded and MUST NOT touch state. */
  #sequence = 0;

  #handleError(error: unknown, mySeq: number): void {
    if (isTimeoutException(error)) {
      this.#handleTimeout(mySeq);
      return;
    }
    if (isAbortException(error)) return;
    if (this.isCurrent(mySeq)) this.setError(this.classifyError(error));
  }

  #handleTimeout(mySeq: number): void {
    if (this.isCurrent(mySeq)) this.setError(this.timeoutMessage());
  }

  /** Applies a successful `response` to store state; called only when the request is still current. */
  protected abstract applyResponse(response: TResponse): void;

  /** Turns a non-abort exception into the user-visible error message the store should surface. */
  protected abstract classifyError(error: unknown): string;

  /** Timeout message subclass surfaces via {@link setError} when the fetcher exceeds its budget. */
  protected abstract timeoutMessage(): string;

  /** Enters the error state with `message`; subclasses implement the concrete state mutation. Exposed as `public` so callers can force an error banner. */
  abstract setError(message: string): void;

  /**
   * Called from subclasses at the top of every user-facing `load()`. Aborts
   * the previous request and returns `{ mySeq, controller }` for the caller
   * to pass to their fetcher.
   */
  protected beginRequest(): { mySeq: number; controller: AbortController } {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    return { mySeq: ++this.#sequence, controller };
  }

  /** Aborts any in-flight fetch and bumps the sequence; subclass `reset()` calls this first. */
  protected abortInFlight(): void {
    this.#controller?.abort();
    this.#controller = undefined;
    this.#sequence += 1;
  }

  /** True iff `mySeq` still matches the current sequence (i.e. the request has not been superseded). */
  protected isCurrent(mySeq: number): boolean {
    return mySeq === this.#sequence;
  }

  /**
   * Runs `fetcher` with a fresh {@link AbortSignal}, superseding any in-flight
   * request. Resolves `true` on success; `false` when aborted, superseded, or
   * failed. Timeouts and non-abort errors set state to `error` only when the
   * request is still current — a stale rejection leaves state to the newer caller.
   */
  protected async runWithSupersede(
    fetcher: (signal: AbortSignal) => Promise<TResponse>,
    timeoutMs: number,
  ): Promise<boolean> {
    const { mySeq, controller } = this.beginRequest();
    const timeoutId = setTimeout(() => {
      controller.abort(new DOMException('timeout', 'TimeoutError'));
    }, timeoutMs);
    try {
      const response = await fetcher(controller.signal);
      if (!this.isCurrent(mySeq)) return false;
      this.applyResponse(response);
      return true;
    } catch (error: unknown) {
      this.#handleError(error, mySeq);
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

/** True when `error` is a `DOMException` produced by an abort with `TimeoutError` name. */
export function isTimeoutException(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'TimeoutError';
}

/** True when `error` is a `DOMException` produced by an abort with `AbortError` name. */
export function isAbortException(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
